import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtUSD } from "@/lib/api";

const pct = (v) => (v == null ? "-" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`);

function Body({ p }) {
  const v = p.values;
  return (
    <div className="space-y-4 text-xs" data-testid="parcel-card-body">
      <div className="grid grid-cols-3 gap-2">
        {[["Market value", v.market_value, "value"], ["Land", v.land_value, "land"], ["Improvements", v.improvement_value, "improvements"]].map(([l, x, k]) => (
          <div key={k} className="rounded-lg bg-slate-50 p-2.5"><div className="text-[10px] text-slate-500">{l}</div><div className="num text-base font-semibold text-slate-900" data-testid={`parcel-card-${k}`}>{x ? fmtUSD(x, true) : "-"}</div></div>))}
      </div>
      <div className="text-[10px] text-slate-500">Tax year {v.tax_year}{v.appraised_value ? ` · appraised ${fmtUSD(v.appraised_value, true)}` : ""}{v.assessed_value ? ` · assessed ${fmtUSD(v.assessed_value, true)}` : ""}</div>
      <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1" data-testid="parcel-card-details">
        {p.details.map((d) => <div key={d.label} className="contents"><dt className="text-slate-500">{d.label}</dt><dd className="text-slate-900 break-words">{d.value}</dd></div>)}
      </dl>
      <div>
        <div className="eyebrow mb-1">Value by tax year</div>
        {p.history.length ? <table className="w-full" data-testid="parcel-card-history"><tbody>{p.history.map((h) => {
          const y = p.yoy.find((x) => x.year === h.year);
          return <tr key={h.year} className="border-t border-slate-100"><td className="py-1">{h.year}</td><td className="num">{fmtUSD(h.value)}</td><td className={`num ${y?.pct > 0 ? "text-emerald-700" : y?.pct < 0 ? "text-red-600" : "text-slate-400"}`}>{y ? pct(y.pct) : ""}</td><td className="text-[10px] text-slate-400">{h.source === "snapshot" ? "OmniFi snapshot" : "county roll"}</td></tr>;
        })}</tbody></table> : <div className="text-slate-500">No yearly values published.</div>}
      </div>
      <div className="text-[10px] text-slate-500">{p.source} · read {new Date(p.read_at).toLocaleString()}. {p.note}</div>
      <a href={p.county_link} target="_blank" rel="noreferrer" data-testid="parcel-card-county-link" className="inline-flex items-center gap-1 text-sky-700 hover:underline">Open on the county website<ExternalLink className="w-3 h-3" /></a>
    </div>
  );
}

export function ParcelLink({ county, pid, children, testid }) {
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  if (!county || !pid) return children;
  const show = (e) => {
    e.stopPropagation();
    setOpen(true);
    if (!p) { setErr(null); api.get(`/market-comps/parcel/${county}/${encodeURIComponent(pid)}`).then((r) => setP(r.data)).catch((x) => setErr(x.response?.data?.detail || "Could not read the county roll")); }
  };
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={show} data-testid={testid || `parcel-link-${pid}`} className="text-sky-700 hover:underline text-left">{children}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="parcel-card">
          <DialogHeader><DialogTitle className="text-base">Parcel {pid}</DialogTitle><DialogDescription className="text-xs">{p?.district || (county === "dallas" ? "Dallas Central Appraisal District" : "Travis Central Appraisal District")}</DialogDescription></DialogHeader>
          {err ? <div className="text-xs text-red-600" data-testid="parcel-card-error">{err}</div> : p ? <Body p={p} /> : <div className="text-xs text-slate-500" data-testid="parcel-card-loading">Reading the county roll…</div>}
        </DialogContent>
      </Dialog>
    </span>
  );
}
